import get from 'lodash/get';
import orderBy from 'lodash/orderBy';

import { toNumOrZero } from './number';

type AccessorFn<T = unknown> = (entity: T) => unknown;
export type Accessor<T = unknown> = string | AccessorFn<T>;
type Direction = 'asc' | 'desc';
export type SortingType = 'number' | 'string';
export type SortBy<T = unknown> = {
  accessor: Accessor<T> | Accessor<T>[];
  direction: Direction;
  sortingType?: SortingType;
  caseSensitive?: boolean;
};

const accessorsByType = {
  string: (value: unknown) => String(value ?? ''),
  number: (value: unknown) => toNumOrZero(value, { parser: parseFloat }),
} as const;

const getAccessor = <T = unknown>(
  accessor: Accessor<T>,
  sortBy?: SortBy<T>
): AccessorFn<T> => {
  const resolvedAccessor = ((): ((entity: T) => unknown) => {
    if (typeof accessor === 'function') {
      return (entity) => accessor(entity);
    }
    if (sortBy?.sortingType) {
      return (entity) => {
        const value = get(entity, accessor);
        return accessorsByType[sortBy.sortingType!](value);
      };
    }
    return (entity) => get(entity, accessor);
  })();
  if (sortBy?.caseSensitive || sortBy?.sortingType === 'number') {
    return resolvedAccessor;
  }
  return (entity: T): unknown => {
    const value = resolvedAccessor(entity);
    if (typeof value === 'string') {
      return value.toLowerCase();
    }
    return value;
  };
};

export const sort = <T = unknown>(
  data: T[],
  sortBy: SortBy<T> | SortBy<T>[]
): T[] => {
  const accessors: AccessorFn<T>[] = [];
  const directions: Direction[] = [];

  for (const opts of Array.isArray(sortBy) ? sortBy : [sortBy]) {
    for (const accessor of Array.isArray(opts.accessor)
      ? opts.accessor
      : [opts.accessor]) {
      accessors.push(getAccessor(accessor, opts));
      directions.push(opts.direction);
    }
  }

  return orderBy(data, accessors, directions);
};
